import { useCallback } from 'react';

import { useSession } from '@/components/auth';
import { useApi } from '@/hooks/use-api';
import { Alert } from '@/utils/alert';

export function useAuthDebug() {
	const { session, refreshTokens } = useSession();
	const { fetchApi } = useApi();

	const checkTokenStatus = useCallback(async () => {
		try {
			console.log('=== AUTH DEBUG ===');
			if (!session) {
				console.log('No active session');
				return { hasSession: false };
			}

			// Don't log the full tokens for security, just status
			console.log('Session status:', {
				id: session.id,
				email: session.email,
				hasAccessToken: !!session.accessToken,
				hasIdToken: !!session.idToken,
				hasRefreshToken: !!session.refreshToken,
				accessTokenLength: session.accessToken?.length,
				idTokenLength: session.idToken?.length,
				refreshTokenLength: session.refreshToken?.length,
				provider: session.provider,
				tokenUsed: session.idToken ? 'ID Token' : 'Access Token',
			});

			// Test the token - prefer ID token if available
			const authToken = session.idToken || session.accessToken;
			console.log('Testing current token...');
			try {
				const response = await fetchApi('/me');
				if (response.ok) {
					console.log('Token is valid');
					return {
						hasSession: true,
						tokenValid: true,
						tokenType: session.idToken ? 'idToken' : 'accessToken',
					};
				} else {
					console.log('Token is invalid:', response.status);
				}
			} catch (error) {
				console.error('Error testing token:', error);
			}

			// If we get here, the token is invalid. Try refreshing.
			if (session.refreshToken) {
				console.log('Attempting manual token refresh...');
				const newSession = await refreshTokens();

				if (newSession) {
					console.log('Manual token refresh successful');
					return {
						hasSession: true,
						tokenValid: false,
						refreshSuccessful: true,
					};
				} else {
					console.log('Manual token refresh failed');
					return {
						hasSession: true,
						tokenValid: false,
						refreshSuccessful: false,
					};
				}
			}

			return {
				hasSession: true,
				tokenValid: false,
				hasRefreshToken: !!session.refreshToken,
			};
		} catch (error) {
			console.error('Auth debug error:', error);
			return { error };
		}
	}, [session, fetchApi, refreshTokens]);

	const showAuthDebugAlert = useCallback(async () => {
		const status = await checkTokenStatus();
		Alert.alert(
			'Auth Debug',
			`Session: ${status.hasSession ? 'Yes' : 'No'}\n`
			+ `Token Valid: ${status.tokenValid ? 'Yes' : 'No'}\n`
			+ `Refresh Token: ${status.hasRefreshToken ? 'Yes' : 'No'}\n`
			+ `Refresh Attempted: ${status.refreshSuccessful !== undefined ? 'Yes' : 'No'}\n`
			+ `Refresh Success: ${status.refreshSuccessful ? 'Yes' : 'No'}`,
			[{ text: 'OK' }],
		);
	}, [checkTokenStatus]);

	return {
		checkTokenStatus,
		showAuthDebugAlert,
	};
}
